import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Mail, Check } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import PrivacyNotice from "@/components/PrivacyNotice";

const CATEGORIES = [
  "Literary Fiction", "Essays", "Poetry", "Short Stories",
  "Historical Fiction", "Business", "Non-Fiction", "Memoir",
];

const STORAGE_KEY = "folio_subscribed";

export default function NewsletterSignup({ variant = "footer" }) {
  const [step, setStep] = useState(1); // 1 = email, 2 = name+cats, 3 = done
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) === "true") setStep(3);
  }, []);

  const toggleCat = (c) => {
    setCats((cs) => cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]);
  };

  const submitStep1 = (e) => {
    e.preventDefault();
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(email)) {
      setError("Enter a valid email.");
      return;
    }
    setError("");
    setStep(2);
  };

  const submitStep2 = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/subscribers", { email, name, favorite_categories: cats });
      localStorage.setItem(STORAGE_KEY, "true");
      setStep(3);
      toast.success("You're on the list. Welcome to Folio.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not subscribe");
    } finally {
      setLoading(false);
    }
  };

  // Compact footer variant
  if (variant === "footer") {
    if (step === 3) {
      return (
        <div data-testid="newsletter-footer-success" className="text-sm text-sage flex items-center gap-2">
          <Check className="w-4 h-4" /> You're subscribed.
        </div>
      );
    }
    return (
      <form data-testid="newsletter-footer-form" onSubmit={step === 1 ? submitStep1 : submitStep2} className="space-y-3">
        {step === 1 && (
          <>
            <Input
              data-testid="newsletter-footer-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="bg-paper border-edge rounded-none h-10 text-sm"
            />
            <button data-testid="newsletter-footer-submit" type="submit" className="btn-primary w-full !py-3 text-xs">
              Subscribe
            </button>
            <PrivacyNotice
              testid="newsletter-footer-privacy"
              purpose="We use your email to send Letters from the press."
            />
          </>
        )}
        {step === 2 && (
          <>
            <Input
              data-testid="newsletter-footer-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="First name (optional)"
              className="bg-paper border-edge rounded-none h-10 text-sm"
            />
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleCat(c)}
                  className={`text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${
                    cats.includes(c) ? "bg-ink text-paper border-ink" : "border-edge text-muted_ink hover:border-ink"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <button data-testid="newsletter-footer-finish" type="submit" disabled={loading} className="btn-primary w-full !py-3 text-xs disabled:opacity-60">
              {loading ? "Joining…" : "Join Folio"}
            </button>
          </>
        )}
        {error && <div className="text-xs text-terracotta">{error}</div>}
      </form>
    );
  }

  // Hero / dedicated variant
  if (step === 3) {
    return (
      <div data-testid="newsletter-hero-success" className="border border-edge bg-linen p-10 text-center">
        <Check className="w-10 h-10 mx-auto text-terracotta" strokeWidth={1.25} />
        <div className="font-serif text-3xl mt-4">You're on the list.</div>
        <p className="text-muted_ink mt-2 max-w-md mx-auto text-sm">
          Watch for our next dispatch — new releases, slow reads, and the occasional discount.
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid="newsletter-hero-form"
      onSubmit={step === 1 ? submitStep1 : submitStep2}
      className="border border-edge bg-linen p-8 md:p-12 grain relative"
    >
      <div className="label-eyebrow text-sage mb-3 flex items-center gap-2">
        <Mail className="w-3.5 h-3.5" /> Folio · Dispatches
      </div>
      <h2 className="font-serif text-4xl md:text-5xl tracking-tight">
        Letters from the press.
      </h2>
      <p className="text-muted_ink mt-3 max-w-xl">
        New releases, considered reading lists, and the occasional discount. No noise.
      </p>

      {step === 1 ? (
        <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl">
          <Input
            data-testid="newsletter-hero-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="bg-paper border-edge rounded-none h-12 flex-1"
          />
          <button data-testid="newsletter-hero-continue" type="submit" className="btn-primary">
            Continue
          </button>
        </div>
      ) : (
        <div className="mt-8 space-y-4 max-w-xl">
          <Input
            data-testid="newsletter-hero-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="First name (optional)"
            className="bg-paper border-edge rounded-none h-12"
          />
          <div>
            <div className="label-eyebrow mb-3">Favorite genres (pick any)</div>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleCat(c)}
                  className={`text-xs uppercase tracking-widest px-3 py-2 border transition-colors ${
                    cats.includes(c) ? "bg-ink text-paper border-ink" : "border-edge text-muted_ink hover:border-ink hover:text-ink"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          <button data-testid="newsletter-hero-finish" type="submit" disabled={loading} className="btn-primary disabled:opacity-60">
            {loading ? "Joining…" : "Join Folio"}
          </button>
        </div>
      )}
      {error && <div className="text-sm text-terracotta mt-3">{error}</div>}
      <PrivacyNotice
        testid="newsletter-hero-privacy"
        purpose="We use your email to send Letters from the press."
      />
    </form>
  );
}
